import {
  Almanac,
  Category,
  CategoryChange,
  CategoryCollectionChange,
  DeleteCategoryInTransaction,
  IdPair,
  PlantProfile,
  Role,
  SavedCategory,
  SavedPlantProfile,
} from "protos/robot_syncer/plant_profile";
import { almanacWithoutFineTuneFields, isValidAlmanac } from "../shootTime";
import {
  areCategoriesEqual,
  categoryRolesForCategories,
  getDefaultCategoryNames,
} from "./category";
import { DateTime } from "luxon";
import { i18n as I18n, TFunction } from "i18next";
import { isEqual } from "../equality";
import { localeCompare } from "../strings";
import { PlantProfileChanges } from "protos/portal/category_profile";
import { RobotSummaryResponse } from "protos/portal/robots";
import { v4 as uuid } from "uuid";

export type UnsavedPlantProfile = Omit<
  PlantProfile,
  "categories" | "categoryMemberships"
> & {
  categories: Category[];
  categoryRoles: Record<string, Role>;
};

export const categoryOwnershipFromPlantProfile = (
  profile: Pick<UnsavedPlantProfile, "carbon" | "customerId">
): Pick<Category, "carbon" | "customerId"> => {
  if (profile.carbon) {
    return { carbon: true };
  }
  if (profile.customerId) {
    return { customerId: profile.customerId };
  }
  return {};
};

export const chipIdSetInProfile = (
  categories: readonly { chipIds: readonly string[] }[]
): Set<string> => {
  const ids = new Set<string>();
  for (const category of categories) {
    for (const id of category.chipIds) {
      if (id) {
        ids.add(id);
      }
    }
  }
  return ids;
};

/** Template chip ids that are not already on the profile. Preserves order and keeps each id once. */
export const chipIdsNotInProfile = (
  requestedChipIds: readonly string[],
  existingChipIds: ReadonlySet<string>
): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of requestedChipIds) {
    if (!id || seen.has(id) || existingChipIds.has(id)) {
      continue;
    }
    seen.add(id);
    result.push(id);
  }
  return result;
};

export const membershipRolesFromPairs = (
  memberships: readonly IdPair[] | undefined
): Record<string, Role> => {
  const roles: Record<string, Role> = {};
  for (const membership of memberships ?? []) {
    if (membership.id && membership.role) {
      roles[membership.id] = membership.role;
    }
  }
  return roles;
};

// used to normalize types to the lowest denominator so that things like comparison can occur
export const toUnsavedPlantProfile = (
  pp: SavedPlantProfile
): UnsavedPlantProfile | undefined => {
  const lastSavedCategories: Category[] = [];
  for (const c of pp.profile?.categories ?? []) {
    const unsavedCat = c.category;
    if (!unsavedCat) {
      continue;
    }
    lastSavedCategories.push(unsavedCat);
  }
  if (!pp.profile || !pp.metadata) {
    return undefined;
  }
  return {
    id: pp.profile.id,
    name: pp.profile.name,
    customerId: pp.profile.customerId,
    carbon: pp.profile.carbon,
    categories: lastSavedCategories,
    categoryRoles: categoryRolesForCategories(
      lastSavedCategories,
      membershipRolesFromPairs(pp.profile.categoryMemberships)
    ),
  };
};

const getCategoryCollectionContents = ({
  id,
  name,
}: UnsavedPlantProfile): Pick<UnsavedPlantProfile, "id" | "name"> => ({
  id,
  name,
});
export const areCategoryCollectionsEqual = (
  a?: UnsavedPlantProfile,
  b?: UnsavedPlantProfile
): boolean => {
  const areCCEqual = isEqual(
    a ? getCategoryCollectionContents(a) : undefined,
    b ? getCategoryCollectionContents(b) : undefined,
    { orderedArrays: false }
  );
  // if any of the cats are not equal, the cat will be updated, making a new version which means the collection needs to update
  const areCatsEqual = isEqual(a?.categories ?? [], b?.categories ?? [], {
    orderedArrays: false,
  });
  const areRolesEqual = isEqual(a?.categoryRoles ?? {}, b?.categoryRoles ?? {});
  return areCCEqual && areCatsEqual && areRolesEqual;
};

/**
 * Checks equality between two profiles. Disregards array ordering for both categories and chip ids
 */
export const arePlantProfilesEqual = (
  a?: UnsavedPlantProfile,
  b?: UnsavedPlantProfile
): boolean => isEqual(a, b, { orderedArrays: false });

const isRobotFeatureFlagEnabled = (
  summary: RobotSummaryResponse | undefined,
  featureFlag: string
): boolean => summary?.robot?.featureFlags[featureFlag] ?? false;

export const isRobotClassifyingByPlantProfile = (
  summary?: RobotSummaryResponse
): boolean =>
  isRobotFeatureFlagEnabled(summary, "embedding_based_classification_feature");

/** Robot-reported gate for custom categories / category templates (v2.5+). */
export const isRobotCustomCategoriesFeatureEnabled = (
  summary?: RobotSummaryResponse
): boolean => isRobotFeatureFlagEnabled(summary, "custom_categories_feature");

/** Robot is on Plant Profiles (embeddings) with custom categories enabled. */
export const isRobotClassifyingByCustomCatCompatPlantProfile = (
  summary?: RobotSummaryResponse
): boolean =>
  isRobotClassifyingByPlantProfile(summary) &&
  isRobotCustomCategoriesFeatureEnabled(summary);

export const getActivePlantProfileId = (
  summary?: RobotSummaryResponse
): string | undefined => {
  const isEnabled = isRobotClassifyingByPlantProfile(summary);
  return isEnabled
    ? summary?.robot?.health?.fieldConfig?.activeCategoryCollectionId
    : undefined;
};

export const isPlantProfileActive = (
  profileId: string,
  summary?: RobotSummaryResponse
): boolean => {
  return profileId === getActivePlantProfileId(summary);
};

/** True when a customer robot is classifying by this profile. */
export const isPlantProfileActiveOnFleet = (
  profileId: string,
  summaries: RobotSummaryResponse[] | undefined,
  customerUuid: string
): boolean => {
  if (!profileId || !customerUuid || !summaries) {
    return false;
  }
  return summaries.some(
    (summary) =>
      summary.customer?.uuid === customerUuid &&
      isPlantProfileActive(profileId, summary)
  );
};

interface ActiveRobotRef {
  serial: string;
  displayName: string;
}
/** Robots in the customer's fleet, grouped by the plant profile they are actively running. */
export const getRobotsByActiveProfileId = (
  summaries: RobotSummaryResponse[] | undefined,
  customerUuid: string,
  locale: string
): Map<string, ActiveRobotRef[]> => {
  if (!summaries || !customerUuid) {
    return new Map();
  }

  const robotsByProfileId = new Map<string, ActiveRobotRef[]>();
  for (const summary of summaries) {
    if (summary.customer?.uuid !== customerUuid) {
      continue;
    }
    const profileId = getActivePlantProfileId(summary);
    if (!profileId) {
      continue;
    }
    const serial = summary.robot?.serial ?? "";
    const displayName = summary.robot?.customerSerial || serial;
    const robots = robotsByProfileId.get(profileId);
    if (robots) {
      robots.push({ serial, displayName });
    } else {
      robotsByProfileId.set(profileId, [{ serial, displayName }]);
    }
  }

  for (const robots of robotsByProfileId.values()) {
    robots.sort((a, b) => localeCompare(a.displayName, b.displayName, locale));
  }
  return robotsByProfileId;
};

export const getBlankPlantProfile = (): UnsavedPlantProfile => ({
  id: uuid(),
  name: "",
  categories: [],
  categoryRoles: {},
});

export const getDefaultPlantProfile = (
  t: TFunction,
  i18n: boolean = true
): UnsavedPlantProfile => {
  const unsavedCategories: Category[] = getDefaultCategoryNames(t, i18n).map(
    (name) => ({
      id: uuid(),
      name,
      chipIds: [],
    })
  );
  return {
    id: uuid(),
    name: "",
    categories: unsavedCategories,
    categoryRoles: categoryRolesForCategories(unsavedCategories),
  };
};

export const deletePlantProfileChanges = (
  profile: UnsavedPlantProfile
): PlantProfileChanges => {
  return {
    categories: profile.categories.map((c) => ({
      delete: { id: c.id },
    })),
    collection: {
      delete: { id: profile.id },
    },
  };
};

export const revertPlantProfileChanges = (
  ccID: string,
  parentVersionId: string,
  revertVersionId: string
): PlantProfileChanges => {
  return {
    categories: [],
    collection: {
      update: {
        metadata: {
          versionId: uuid(),
          parentVersionId,
          active: true,
        },
        revert: {
          id: ccID,
          revertVersionId,
        },
      },
    },
  };
};

// Note that the Backend is responsible for determining and assigning ownership (customer vs carbon)
export const determinePlantProfileChanges = (
  saved: SavedPlantProfile | undefined,
  unsaved: UnsavedPlantProfile
): PlantProfileChanges => {
  const categoryChanges: CategoryChange[] = [];
  const categoryMembershipChanges: IdPair[] = [];

  const savedCatMap: { [cId: string]: SavedCategory } = {};
  for (const c of saved?.profile?.categories || []) {
    const cId = c.category?.id;
    if (!cId) {
      continue;
    }
    savedCatMap[cId] = c;
  }

  for (const c of unsaved.categories) {
    if (!c.id) {
      throw new Error("Category ID cannot be  empty");
    }
    const category: Category = {
      id: c.id,
      name: c.name,
      chipIds: c.chipIds,
      customerId: c.customerId,
      carbon: c.carbon,
      almanac: c.almanac ? almanacWithoutFineTuneFields(c.almanac) : undefined,
    };
    const role = unsaved.categoryRoles[c.id];
    if (!role) {
      throw new Error(`Role for category ${c.id} is undefined`);
    }
    const membershipIdPair = (versionId: string): IdPair => ({
      id: c.id,
      versionId,
      role,
    });

    if (!saved || !c.id || !savedCatMap[c.id]) {
      const cvID = uuid();
      // create category
      categoryChanges.push({
        create: {
          metadata: {
            versionId: cvID,
          },
          category,
        },
      });
      categoryMembershipChanges.push(membershipIdPair(cvID));
    } else {
      const savedCategory = savedCatMap[c.id];
      if (
        savedCategory?.category &&
        savedCategory.metadata &&
        !areCategoriesEqual(savedCategory.category, category)
      ) {
        // update category
        const cvID = uuid();
        categoryChanges.push({
          update: {
            metadata: {
              versionId: cvID,
              parentVersionId: savedCategory.metadata.versionId,
              active: true,
              lastKnownActiveVersionId: savedCategory.metadata.versionId,
            },
            category,
          },
        });
        categoryMembershipChanges.push(membershipIdPair(cvID));
      } else {
        // unchanged
        categoryMembershipChanges.push(
          membershipIdPair(savedCategory?.metadata?.versionId ?? "")
        );
      }
    }
  }

  const unsavedCategoryIds = new Set(unsaved.categories.map(({ id }) => id));
  for (const [cId, savedCategory] of Object.entries(savedCatMap)) {
    if (unsavedCategoryIds.has(cId)) {
      continue;
    }
    // delete category being removed from collection
    const deleteCategory: DeleteCategoryInTransaction = { id: cId };
    if (savedCategory.metadata?.versionId) {
      deleteCategory.metadata = {
        parentVersionId: savedCategory.metadata.versionId,
        active: true,
        lastKnownActiveVersionId: savedCategory.metadata.versionId,
      };
    }
    categoryChanges.push({ delete: deleteCategory });
  }

  let collectionChange: CategoryCollectionChange | undefined;
  if (!saved || !saved.profile?.id) {
    if (!unsaved.id) {
      throw new Error("Collection ID cannot be empty");
    }
    // create category collection
    collectionChange = {
      create: {
        metadata: {
          versionId: uuid(),
        },
        collection: {
          id: unsaved.id,
          name: unsaved.name,
          carbon: unsaved.carbon,
          customerId: unsaved.customerId,
          categoryIds: categoryMembershipChanges,
        },
      },
    };
  } else if (
    saved.metadata &&
    !areCategoryCollectionsEqual(toUnsavedPlantProfile(saved), unsaved)
  ) {
    collectionChange = {
      update: {
        metadata: {
          versionId: uuid(),
          parentVersionId: saved.metadata.versionId,
          active: true,
          lastKnownActiveVersionId: saved.metadata.versionId,
        },
        collection: {
          id: unsaved.id,
          name: unsaved.name,
          carbon: unsaved.carbon,
          customerId: unsaved.customerId,
          categoryIds: categoryMembershipChanges,
        },
      },
    };
  }

  return {
    collection: collectionChange,
    categories: categoryChanges,
  };
};

/**
 * Saved shoot time to restore when this category has one and the draft differs from it.
 * Undefined when the category is unsaved, the saved shoot time is empty, or nothing changed.
 */
export const tryGetResettableAlmanacBaseline = (
  categoryId: string,
  categories: readonly Pick<Category, "id" | "almanac">[] | undefined,
  savedCategories: readonly Pick<Category, "id" | "almanac">[] | undefined
): Almanac | undefined => {
  const baseline = savedCategories?.find(
    (category) => category.id === categoryId
  )?.almanac;
  if (!isValidAlmanac(baseline)) {
    return undefined;
  }
  const current = categories?.find(
    (category) => category.id === categoryId
  )?.almanac;
  return isEqual(current, baseline) ? undefined : baseline;
};

export const formatReadableDate = (dateString: string, i18n: I18n): string => {
  const date = DateTime.fromISO(dateString);
  if (!date.isValid) {
    return "";
  }

  return date.toFormat("MMMM d, h:mm a", { locale: i18n.language });
};

export const areDatesClose = (
  dateStringA: string,
  dateStringB: string,
  minutes = 1
): boolean =>
  Math.abs(
    DateTime.fromISO(dateStringA).diff(DateTime.fromISO(dateStringB)).minutes
  ) > minutes;
